import {
  Constants,
  CoverageTypesCodes,
  PopulationType,
  UpdatePcpErrorResponse,
} from '../common/Constants';
import { convertObjectToUrlParams } from '../common/Utils';
import { formatDateWithoutLeadingZeros } from '../common/Utils/datesUtils/formatDate';
import {
  isCnsMember,
  isIfpMember,
  isMnrMember,
} from '../common/Utils/memberUtils/memberUtils';
import { NetworkDetails, ProviderLocation } from '../models/ProviderDetails';
import { getMemberHealthCoverageType } from './providerSearch.utils';
import { removeLeadingZeroes } from './string.utils';

/**
 * Collects all pcpIds from provider locations (location-level and networkAffiliation-level)
 * and checks if the given memberPcpID matches any of them.
 */
export const hasMatchingPcpId = (
  providerLocations: ProviderLocation[] | undefined,
  memberPcpID?: string
): boolean => {
  if (!memberPcpID || !Array.isArray(providerLocations)) return false;

  const allPcpIdsFromLocations: string[] = providerLocations.flatMap(
    (location) => {
      const locationPcpId = location?.pcpId ? [location.pcpId] : [];
      const networks = (location?.networks ||
        []) as unknown as NetworkDetails[];
      const affiliationPcpIds = networks.flatMap(
        (n) =>
          (n?.networkAffiliations || [])
            .map((aff) => aff?.pcpId)
            .filter(Boolean) as string[]
      );
      return [...locationPcpId, ...affiliationPcpIds];
    }
  );

  return allPcpIdsFromLocations.some(
    (id) => removeLeadingZeroes(id) === removeLeadingZeroes(memberPcpID)
  );
};

export const getPCPGetParams = (
  enablePreProd: boolean,
  loggedInMember,
  memberId: string
) => {
  const {
    demographics,
    eligibility,
    familyId,
    isGatedUser,
    isNonGatedUSPUser,
    lineOfBusiness,
    membershipCategory,
    policyNumber,
    population,
    reciprocityId,
  } = loggedInMember;

  const { name, dateOfBirth, dependentSeqNbr } = demographics;

  const medicalCoverage =
    getMemberHealthCoverageType(eligibility, CoverageTypesCodes.MEDICAL) || {};
  const {
    claimSystemTypeCode,
    coverageID,
    eligibilitySystemTypeCode,
    encryptedUserNameWithPlanId,
    marketType,
    planVariationCode,
  } = medicalCoverage;

  const coverageId = coverageID?.find(
    (cov) => cov.sourceSysCode === 'SUBSCRIBER ID'
  );

  return {
    firstName: name.firstName,
    lastName: name.lastName,
    groupNumber: policyNumber,
    memberId,
    claimSystemTypeCode,
    dateOfBirth,
    dependentSeqNbr,
    eligibilitySystemTypeCode,
    enablePreProd,
    familyId,
    id: encryptedUserNameWithPlanId,
    isGatedUser,
    isNonGatedUSPUser,
    lob: lineOfBusiness,
    membershipCategory,
    planVariationCode,
    population,
    portalSource: marketType,
    reciprocityId,
    userName: coverageId?.id,
  };
};

export const resolvePcpId = (
  pcpId: string,
  lineOfBusiness: string,
  selectedNetworkAffiliation: any = null,
  isUspNiceMember: boolean = false
): string =>
  // For M&R, IFP and CnS LOB and For ENI Nice Migrated members, use network affiliation pcpId if available
  (isMnrMember(lineOfBusiness) ||
    isUspNiceMember ||
    isIfpMember(PopulationType[lineOfBusiness]) ||
    isCnsMember(lineOfBusiness)) &&
  selectedNetworkAffiliation?.pcpId
    ? selectedNetworkAffiliation.pcpId
    : pcpId;

const splitUspNicePcpIdentifier = (pcpIdentifier: string) => {
  const normalizedPcpIdentifier = pcpIdentifier?.trim();

  if (!normalizedPcpIdentifier || normalizedPcpIdentifier.length < 20) {
    return null;
  }

  return {
    providerID: normalizedPcpIdentifier.slice(0, 10),
    networkTypeCode: normalizedPcpIdentifier.slice(-10),
  };
};

export const getPCPUpdateParams = (
  dependentSeqNbr: string | undefined,
  loggedInMember,
  memberByDependentSeqNbr,
  memberId: string,
  pcpId: string,
  providerType: string,
  providerName: string,
  guestData: any = {},
  isIFPEarlyPay: boolean = false,
  selectedNetworkAffiliation: any = null,
  isUspNiceMember: boolean = false
) => {
  if (isIFPEarlyPay) {
    return {
      exchangeId: guestData?.exchangeId,
      pcpId,
      stateCode: guestData?.stateCode,
      qhpId: guestData?.qhpId,
      isIFPEarlyPay,
    };
  }

  const { demographics: loggedInDemographics, population } = loggedInMember;
  const { dependentSeqNbr: loggedInDepSeqNbr } = loggedInDemographics;
  const {
    demographics,
    eligibility,
    familyId,
    isGatedUser,
    isNonGatedUSPUser,
    lineOfBusiness,
    policyNumber,
  } = memberByDependentSeqNbr;
  const { dateOfBirth, name } = demographics;
  const { firstName, lastName } = name;

  const medicalCoverage =
    getMemberHealthCoverageType(eligibility, CoverageTypesCodes.MEDICAL) || {};
  const { coverageID, encryptedUserNameWithPlanId, marketType } =
    medicalCoverage;

  const coverageId = coverageID?.find(
    (cov) => cov.sourceSysCode === 'SUBSCRIBER ID'
  );
  const resolvedPcpId = resolvePcpId(
    pcpId,
    lineOfBusiness,
    selectedNetworkAffiliation,
    isUspNiceMember
  );
  const uspNicePcpIdentifier = isUspNiceMember
    ? splitUspNicePcpIdentifier(resolvedPcpId)
    : null;

  return {
    firstName,
    lastName,
    dateOfBirth,
    groupNumber: policyNumber,
    memberId,
    dependentSeqNbr,
    familyId,
    pcpId: uspNicePcpIdentifier?.providerID || resolvedPcpId,
    pcpName: providerName,
    submittedByDependentSequenceNumber: loggedInDepSeqNbr,
    providerType,
    isGatedUser,
    isNonGatedUSPUser,
    id: encryptedUserNameWithPlanId,
    lob: lineOfBusiness,
    portalSource: marketType,
    userName: coverageId?.id,
    population,
    networkTypeCode: uspNicePcpIdentifier?.networkTypeCode,
  };
};

// Returns the lagoon key value to pull error modal content from the Update PCP Error Modal table
export const getErrorModalContentKey = (
  populationType: PopulationType,
  errorCode: string = '',
  errorMessage: string = ''
): string => {
  const message = errorMessage?.toLowerCase();
  if (populationType === PopulationType.USP) {
    switch (errorCode) {
      case '50127':
        return '3';
      default:
        return '2';
    }
  }

  if (
    (errorCode === '000' &&
      message === UpdatePcpErrorResponse.noPrimaryCareRecords) ||
    (errorCode === '500352' &&
      message === UpdatePcpErrorResponse.noPcpNetworkFound)
  ) {
    return '1';
  }

  switch (errorCode) {
    case '40423':
    case '20003':
      return '1';
    default:
      return '2';
  }
};

export type UpdatePCPErrorContent = {
  dntPopulation?: string;
  dntObapiErrorCode?: string;
  dntObapiErrorResponse?: string;
  dntUpdatePcpErrorModalTableKey?: string;
};

export const getErrorMessageKeyUsingPCPErrorMapping = (
  population: PopulationType,
  errorCode: string = '',
  errorMessage: string = '',
  errorTableContent?: UpdatePCPErrorContent[]
) => {
  if (errorTableContent == null || errorTableContent.length === 0) {
    return getErrorModalContentKey(population, errorCode, errorMessage);
  }

  // Filter error content by population and if there's a match, return the error message key
  const errorContentMatchesByPopulation = errorTableContent.filter(
    (item) => item.dntPopulation?.toLowerCase() === population?.toLowerCase()
  );
  if (
    errorContentMatchesByPopulation != null &&
    errorContentMatchesByPopulation.length > 0
  ) {
    const errorMessageKey = getErrorMessageKey(
      errorCode,
      errorMessage,
      errorContentMatchesByPopulation
    );
    if (errorMessageKey) {
      return errorMessageKey;
    }
  }

  // If there were no error matches from the population filter, filter by dntPopulation column not defined/empty and return an error message key
  const errorContentWithUndefinedDntPopulation = errorTableContent.filter(
    (item) =>
      item.dntPopulation === '' ||
      item.dntPopulation === undefined ||
      item.dntPopulation === null
  );

  return (
    getErrorMessageKey(
      errorCode,
      errorMessage,
      errorContentWithUndefinedDntPopulation
    ) ?? '2'
  );
};

const getErrorMessageKey = (
  errorCode: string,
  errorResponse: string,
  errorTableContent: UpdatePCPErrorContent[]
) => {
  const matchesByErrorCode = errorTableContent.filter(
    (item) => item.dntObapiErrorCode === errorCode
  );

  const matchByErrorResponse = matchesByErrorCode.find(
    (item) =>
      item.dntObapiErrorResponse?.toLowerCase() === errorResponse.toLowerCase()
  );
  if (matchByErrorResponse) {
    return matchByErrorResponse.dntUpdatePcpErrorModalTableKey;
  }

  const emptyErrorResponseMatch = matchesByErrorCode.find(
    (item) =>
      item.dntObapiErrorResponse === '' || item.dntObapiErrorResponse == null
  );
  if (emptyErrorResponseMatch) {
    return emptyErrorResponseMatch.dntUpdatePcpErrorModalTableKey;
  }

  const defaultErrorCodeMatch = errorTableContent.find(
    (item) => item.dntObapiErrorCode?.toLowerCase() === 'default'
  );
  return defaultErrorCodeMatch?.dntUpdatePcpErrorModalTableKey ?? null;
};

export const getShowEditPCP = (editPcp, lockInIndicator) =>
  editPcp && !lockInIndicator;

export const getIsFuturePCPCardSeparate = (lob) =>
  isMnrMember(lob) || isCnsMember(lob);

export const showFuturePCPText = (lob, futurePCP, isPreEffective) =>
  !!futurePCP && !isPreEffective && !getIsFuturePCPCardSeparate(lob);

export const showFuturePCPCard = (lob, futurePCP, isPreEffective) =>
  !!futurePCP && !isPreEffective && getIsFuturePCPCardSeparate(lob);

export const doChoosePCPStoreUpdate = (
  dependentSeqNbr,
  chipStore,
  setTypeaheadSearchStore,
  setPCPSearchState
) => {
  const typeaheadStoreValues = {
    search: Constants.RESULT_SECTION.PRIMARY_CARE_PROVIDERS,
    searchType: Constants.SEARCH_TYPES.SPECIALTY,
    providerType: '',
  };
  setTypeaheadSearchStore({
    ...typeaheadStoreValues,
    resultType: Constants.RESULT_SECTION.ALL,
    acceptingNewPatients: true,
  });
  const pcpStoreValues = {
    choosePCP: true,
    dependentSeqNbr,
    pcpIndicator: true,
  };
  setPCPSearchState(pcpStoreValues);

  return convertObjectToUrlParams(chipStore, {
    ...typeaheadStoreValues,
    ...pcpStoreValues,
  });
};

export const isCnsRestrictedEditPcp = (
  customMesssges,
  memberReportingCode,
  memberPolicyNumber
): boolean =>
  customMesssges?.filter((message) => {
    const { active, dntReportingCode, policyNumber } = message;
    const reportingCodes = dntReportingCode?.trim()?.split(',') || [];
    const isPolicyNumberMatch = policyNumber === memberPolicyNumber;
    return (
      isPolicyNumberMatch &&
      active &&
      (reportingCodes ? reportingCodes.includes(memberReportingCode) : false)
    );
  })?.length > 0;

export const getFuturePcpValues = (message: string, futurePCP: any): string => {
  if (!message || !futurePCP) {
    return message;
  }

  const regexPattern = /\{([^}]+)\}/g;
  return message?.replace(regexPattern, (match, variableName) => {
    const propertyName = variableName?.trim();

    if (propertyName in futurePCP) {
      const value = futurePCP[propertyName as keyof typeof futurePCP];

      if (propertyName === 'startDate' || propertyName === 'endDate') {
        return value != null ? formatDateWithoutLeadingZeros(value) : match;
      }

      return value != null ? String(value) : match;
    }
    return match;
  });
};

export const calculateTotalProviderCount = (
  flag: boolean,
  baseCount: number,
  hasPCP: boolean,
  hasFuturePCP: boolean
): number => {
  let totalCount = baseCount;
  if (hasPCP) totalCount += 1;
  if (flag && hasFuturePCP) totalCount += 1;
  return totalCount;
};

export const shouldShowProviderLimitBanner = (
  enableNewFlow: boolean,
  bookmarkedProvidersLength: number,
  careTeamProviderCount: number,
  maximumProviderLimit: string | undefined,
  hidePCPCard: boolean
): boolean => {
  const maxLimit = Number(maximumProviderLimit);
  return enableNewFlow
    ? bookmarkedProvidersLength >= maxLimit
    : careTeamProviderCount >= maxLimit - (hidePCPCard ? 0 : 1);
};
